import { createEntityAdapter, createSlice } from "@reduxjs/toolkit";
import { FaroCoreApiUtils } from "@stellar/core-api-client";
import { removeMemberFromCompany, updateMemberRoleInWorkspace } from "@store/members/members-slice-thunk";
import { addMembersToTeam, createTeam, deleteTeam, fetchTeamAsMember, fetchTeamDetails, fetchTeamIdsOfMember, fetchTeamMembers, fetchTeams, removeMembersFromTeam, updateTeamDetails } from "@store/teams/teams-slice-thunk";
import { removeLocalMemberFromTeam } from "@store/teams/teams-slice-utils";
export const teamAdapter = createEntityAdapter({
  selectId: team => team.id
});
export const initialState = {
  ...teamAdapter.getInitialState(),
  selectedTeam: {
    id: null,
    groups: null,
    teamAsMember: null,
    members: {},
    membersSortBy: null,
    membersSearch: null,
    totalCount: 0
  },
  fetching: {
    status: "uninitialized",
    isRemovingTeam: false,
    isRemovingTeamMember: false,
    isFetchingTeamMembers: "uninitialized",
    isFetchingTeamDetails: "uninitialized",
    hasFetchedTeams: false,
    isFetchingMoreMembers: false
  },
  nextTeamMember: null,
  isDetailOpened: false,
  memberTeams: {
    memberIdentity: null,
    ids: [],
    status: "uninitialized"
  },
  teamIdsByMember: {}
};
const teamSlice = createSlice({
  name: "teams",
  initialState,
  reducers: {
    resetTeamState: () => initialState,
    openDetailPanel: state => {
      state.isDetailOpened = true;
    },
    closeDetailPanel: state => {
      state.isDetailOpened = false;
    },
    resetMemberTeams: state => {
      state.memberTeams = initialState.memberTeams;
    },
    removeMembersByEmails: (state, action) => {
      const emailsToRemove = new Set(action.payload);
      Object.values(state.entities).forEach(team => {
        team.sampleMembers = team.sampleMembers.filter(member => !emailsToRemove.has(member.userResponse.email));
      });
    }
  },
  extraReducers: builder => {
    builder.addCase(fetchTeams.pending, (state, action) => {
      // Member-scoped fetches are tracked under "memberTeams".
      if (action.meta?.arg.memberIdentity) {
        state.memberTeams.status = "pending";
        state.memberTeams.requestId = action.meta.requestId;
        return;
      }
      state.fetching.status = "pending";
    }).addCase(fetchTeams.fulfilled, (state, action) => {
      // Member-scoped fetches are tracked under "memberTeams".
      const memberIdentity = action.meta?.arg.memberIdentity;
      if (memberIdentity) {
        // A response of an earlier or reset request is outdated, so it must not change anything
        if (state.memberTeams.requestId !== action.meta.requestId) {
          return;
        }
        teamAdapter.upsertMany(state, action.payload);
        state.memberTeams.status = "succeeded";
        state.memberTeams.memberIdentity = memberIdentity;
        state.memberTeams.ids = action.payload.map(team => team.id);
        return;
      }
      state.fetching.status = "succeeded";
      teamAdapter.upsertMany(state, action.payload);

      // Set the flag to know if the teams have been fetched for the first time.
      if (!state.fetching.hasFetchedTeams) {
        state.fetching.hasFetchedTeams = true;
      }
    }).addCase(fetchTeams.rejected, (state, action) => {
      // Member-scoped fetches are tracked under "memberTeams".
      if (action.meta?.arg.memberIdentity) {
        if (state.memberTeams.requestId === action.meta.requestId) {
          state.memberTeams.status = "rejected";
        }
        return;
      }
      state.fetching.status = "rejected";
    }).addCase(fetchTeamIdsOfMember.pending, (state, action) => {
      state.teamIdsByMember[action.meta.arg.memberIdentity] = {
        status: "pending",
        teamIds: [],
        requestId: action.meta.requestId
      };
    }).addCase(fetchTeamIdsOfMember.fulfilled, (state, action) => {
      const memberTeamIds = state.teamIdsByMember[action.meta.arg.memberIdentity];
      // The teams of the member were forgotten while they were fetched, so they may be outdated
      if (memberTeamIds?.requestId !== action.meta.requestId) {
        return;
      }
      memberTeamIds.status = "succeeded";
      memberTeamIds.teamIds = action.payload;
    }).addCase(fetchTeamIdsOfMember.rejected, (state, action) => {
      const memberTeamIds = state.teamIdsByMember[action.meta.arg.memberIdentity];
      if (memberTeamIds?.requestId === action.meta.requestId) {
        memberTeamIds.status = "rejected";
      }
    }).addCase(createTeam.fulfilled, (state, action) => {
      teamAdapter.setOne(state, action.payload);
    })

    // Adding members leaves `fetching.status` alone, which the loading of the teams list relies on
    .addCase(addMembersToTeam.fulfilled, (state, action) => {
      const teamId = action.payload.teamId;
      const team = state.entities[teamId];
      // Members can be added from outside the team page (e.g. the workspace members list), so they
      // only go to the team page's list when it belongs to this team, never to another team's
      const isSelectedTeam = state.selectedTeam.id === teamId;
      if (team) {
        team.memberCount += action.payload.data.length;
      }
      for (const member of action.payload.data) {
        if (isSelectedTeam) {
          const id = FaroCoreApiUtils.Member.getMemberId(member.userResponse);
          state.selectedTeam.members[id] = member;
        } else {
          team?.sampleMembers.push(member);
        }
      }
      if (isSelectedTeam) {
        state.selectedTeam.totalCount += action.payload.data.length;
      }

      // The members are in another team now, so their teams are looked up again when needed
      state.teamIdsByMember = initialState.teamIdsByMember;
    }).addCase(fetchTeamDetails.pending, state => {
      state.fetching.isFetchingTeamDetails = "pending";
    }).addCase(fetchTeamDetails.fulfilled, (state, action) => {
      state.selectedTeam.id = action.payload.id;
      state.fetching.isFetchingTeamDetails = "succeeded";
      teamAdapter.setOne(state, action.payload);
    }).addCase(fetchTeamDetails.rejected, state => {
      state.fetching.isFetchingTeamDetails = "rejected";
    }).addCase(fetchTeamAsMember.pending, state => {
      state.fetching.status = "pending";
    }).addCase(fetchTeamAsMember.fulfilled, (state, action) => {
      state.selectedTeam.teamAsMember = action.payload;
      state.fetching.status = "succeeded";
    }).addCase(fetchTeamAsMember.rejected, state => {
      state.fetching.status = "rejected";
    }).addCase(updateTeamDetails.pending, state => {
      state.fetching.status = "pending";
    }).addCase(updateTeamDetails.fulfilled, (state, action) => {
      state.selectedTeam.id = action.payload.id;
      state.fetching.status = "succeeded";
      teamAdapter.updateOne(state, {
        id: action.payload.id,
        changes: action.payload
      });
    }).addCase(updateTeamDetails.rejected, state => {
      state.fetching.status = "rejected";
    }).addCase(removeMembersFromTeam.pending, state => {
      state.fetching.isRemovingTeamMember = true;
    }).addCase(removeMembersFromTeam.fulfilled, (state, action) => {
      removeLocalMemberFromTeam(state, action.payload);
      state.fetching.isRemovingTeamMember = false;
      // The members left the team, so their teams are looked up again when needed
      state.teamIdsByMember = initialState.teamIdsByMember;
    }).addCase(removeMembersFromTeam.rejected, state => {
      state.fetching.isRemovingTeamMember = false;
    }).addCase(deleteTeam.pending, state => {
      state.fetching.isRemovingTeam = true;
    }).addCase(deleteTeam.fulfilled, (state, action) => {
      teamAdapter.removeOne(state, action.payload);
      state.fetching.isRemovingTeam = false;
    }).addCase(deleteTeam.rejected, state => {
      state.fetching.isRemovingTeam = false;
    }).addCase(fetchTeamMembers.pending, (state, action) => {
      // If nextTeamMember is set, this is a pagination fetch
      if (action.meta.arg.start) {
        state.fetching.isFetchingMoreMembers = true;
      }
      state.fetching.isFetchingTeamMembers = "pending";
    }).addCase(fetchTeamMembers.fulfilled, (state, action) => {
      // Clear previous members only if this is a non-pagination fetch (first load, sort change)
      if (!action.meta.arg.start) {
        state.selectedTeam.members = {};
        state.selectedTeam.membersSortBy = action.meta.arg.sortBy ?? null;
        state.selectedTeam.membersSearch = action.meta.arg.search ?? null;
      }
      state.nextTeamMember = action.payload.next ?? null;
      state.selectedTeam.totalCount = action.payload.totalCount;
      for (const member of action.payload.members) {
        const id = FaroCoreApiUtils.Member.getMemberId(member.userResponse);
        state.selectedTeam.members[id] = member;
      }
      state.fetching.isFetchingMoreMembers = false;
      state.fetching.isFetchingTeamMembers = "succeeded";
    }).addCase(fetchTeamMembers.rejected, (state, _action) => {
      state.fetching.isFetchingMoreMembers = false;
      state.fetching.isFetchingTeamMembers = "rejected";
    }).addCase(removeMemberFromCompany.fulfilled, (state, action) => {
      const memberId = action.payload.identity;
      if (state.selectedTeam.members[memberId]) {
        delete state.selectedTeam.members[memberId];
        state.selectedTeam.totalCount -= 1;
      }
    }).addCase(updateMemberRoleInWorkspace.fulfilled, (state, action) => {
      // Need this case to update team role while updating team role as workspace member from overview page
      if (state.selectedTeam.teamAsMember && state.selectedTeam.teamAsMember.identity === action.payload.identity) {
        state.selectedTeam.teamAsMember.role = action.payload.role;
      }
    });
  }
});
export const {
  resetTeamState,
  openDetailPanel,
  closeDetailPanel,
  removeMembersByEmails,
  resetMemberTeams
} = teamSlice.actions;
export const teamReducer = teamSlice.reducer;